import React, {  useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid,  
	RCCTooltip, 
	RCCInput,
	RCCDropDownItem,
	RCCIcon
} from "react-common-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import Pagination from "src/components/Pagination";
import formatDate from "src/utils/formatDate";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import { useAuth } from "src/context/Auth/context";
import AppIcons from "src/components/AppIcons";
import Modals from "./modals";
import { IFilters, IRefITermsModals } from "./interface";

const Terms: React.FC = () => { 
	const { translate, currentLanguage } = useTranslation();
	const refModals = useRef<IRefITermsModals>(null);
	const {superUser} = useAuth();
	const hasPrivileges = !!superUser;

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IFilters>({ filter: "", type: ""});
	const {handleSubmit : handleSubmitFilters, control: controlFilters, register : registerFilters, reset:resetFilters, watch: watchFilters,setValue:setValueFilters } = useForm();
	
	const submitFilters = (data: IFilters) => {
		setFilters(() => {
			const newState = {} as any;
			newState.filter = data.filter;
			newState.active = data.type;

			if(data.dtStart)
				newState.dtStart = formatDate(data.dtStart) ;
			if(data.dtEnd)
				newState.dtEnd = formatDate(data.dtEnd);

			return newState;
		});
		paginationRef.current?.reload();
	};

	const paginationRef = useRef<IForwardPagination>(null);


	const colTipoTermo = (row: any) => (
		<>
			{row.tea001Tipo === 1 ? translate("Termo de uso"): translate("Política de privacidade")}
		</>
	);

	const colVigencia = (row: any) => (
		<>
			{formatDate(row.tea001Dtinicio, currentLanguage)} {translate("até")} {formatDate(row.tea001Dtfim, currentLanguage)}
		</>
	);

	const colOptions = (row: any) => (	
		<OptionsColumn>
		  <RCCDropDownItem
			  selfEnclose
			  onClick={() => { refModals.current?.actionButtonChange(row);  }}
		  >
			  <RCCIcon name="pencil" 
			  />
			  {translate("Alterar")}
		  </RCCDropDownItem>
		  <RCCDropDownItem 
			  selfEnclose
			  onClick={() => { refModals.current?.actionButtonDelete(row); }}
		  >
			  <RCCIcon name="trash" 
			  />
			  {translate("Excluir")}
		  </RCCDropDownItem>
	  </OptionsColumn>
	);	

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Termos de Aceitação")}
					icon={() => <AppIcons name="configs" />}
				>
					{hasPrivileges && (
						<RCCTooltip
							text={translate("Novo termo de aceitação")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => { refModals.current?.actionButtonAdd(); }}
								size="md"
							/>
						</RCCTooltip>
					)}
					
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || filters.filter || filters.type || filters.dtStart || filters.dtEnd ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || filters.filter || filters.type || filters.dtStart || filters.dtEnd ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>		
				
				{/* Filters */}
				{showFilters && (
					<FiltersContainer
						onSubmitFilters={handleSubmitFilters(submitFilters)}
						resetFilters={() => resetFilters({ filter: "", active: {value: "T", label: translate("Todos")}, dtIni: null, dtEnd: null})}
					>
						<RCCGrid
							container
							item
							spacing={1}
							style={{marginBottom: "2rem"}}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter") || filters.filter}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<DateTimePickerRange
									control={controlFilters}
									nameInit = "dtIni"
									nameEnd = "dtEnd"
									label = {translate("Data")}
									labelSeparator = {translate("até")}
									type = "datePicker"
									defaultValueInit = {watchFilters("dtIni") || null}
									defaultValueEnd = {watchFilters("dtEnd") || null}
									setValue={setValueFilters}
								/>
							</RCCGrid>
						</RCCGrid>
					</FiltersContainer>
				)}	

				{/* List */}	
				<Pagination
					ref={paginationRef}
					dataOrRoute="TermsUse/List"
					filtersData={filters}
					columns={[
	  					{
							idColumn: "tea001Titulo",
							nameColumn: translate("Título"),
							xs: 12,
							sm: 5,
							type: "string",
							truncate: true
	  					},
						{
							idColumn: "tea001Tipo",
							nameColumn: translate("Tipo termo"),
							xs: 12,
							sm: 3,
							cellRenderer: colTipoTermo
	  					},
						{
							idColumn: "tea001Dtinicio",
							nameColumn: translate("Vigência"),
							xs: 12,
							sm: 3,
							cellRenderer: colVigencia
						},
						{
							idColumn: "options",
							nameColumn: " ",
							justify: "right",
							xs: 12,
							sm: 1,
							cellRenderer:  hasPrivileges ? colOptions : () => null,
							disableSort: true
						}
					]}
				/>
			</RCCGrid>
			<Modals ref={refModals} paginationRef={paginationRef} />
		</>
	);
};

export default Terms;
